import axios from 'axios';
import { v4 } from 'uuid';
import {
  BIG_PICTURE_URL,
  AUTH_HEADER_KEY,
  KARROTAUTH_HTTP,
  USER_URL,
  XPROPERTY_URL,
  BUSINESS_AUTH_URL,
  BIZ_PLATFORM_CENTER,
} from 'constants/config';
import { getImageInfo, imageCompress } from 'utils/imageParser';
import { openToast } from 'utils/localDispatcher';
import { store } from 'store';
import { getSupportRegion } from './snippet';
import { jwtDecoder } from 'utils/auth/tokenUtils';
import { generateKarrotUserAgent } from 'utils/userAgentHelper';
import { UserInfoResponse } from 'types/apiTypes';

const bizPlatformCenterInstance = axios.create({
  withCredentials: true,
  baseURL: BIZ_PLATFORM_CENTER,
});

interface imageUploadReqestProps {
  fileItem: File
  userData: {
    id: number
    auth_token: string
  }
  channelId: string
}
export const imageUploadReqest = async ({ fileItem, userData, channelId }: imageUploadReqestProps) => {
  const fileData = await imageCompress(fileItem);
  const { width, height } = await getImageInfo(fileData);
  const bizToken = store.getState().authStore.bizToken;
  const qrLoginToken = store.getState().authStore.storeAccessToken;
  const oidcAccessToken = store.getState().authStore.oidcAccessToken;

  if (bizToken) {
    const requestBody = new FormData();
    requestBody.append('body', fileData);
    requestBody.append('region', getSupportRegion());
    requestBody.append('service', 'rocketchat');
    requestBody.append('type', 'chat');
    requestBody.append('width', `${width}`);
    requestBody.append('height', `${height}`);
    requestBody.append('contentType', fileItem.type);
    requestBody.append('filepath', `rocketchat/${channelId}/${v4()}.${fileItem.type.replace('image/', '')}`);
    try {
      const { data } = await bizPlatformCenterInstance.post('/api/v1/images', requestBody);
      return {
        ...data.data,
        width,
        height,
      };
    } catch (_) {
      openToast('파일 전송에 실패했어요');
    }
  } else if (oidcAccessToken) {
    const requestBody = {
      user_id: Number(userData.id),
      service: 'rocketchat',
      type: 'chat',
      width: width,
      height: height,
      filepath: `rocketchat/${channelId}/${v4()}.${fileItem.type.replace('image/', '')}`,
      content_type: fileItem.type,
    };
    const requestOption = {
      headers: {
        'Authorization': `Bearer ${oidcAccessToken}`,
        'Content-Type': 'application/json',
      },
    };
    try {
      const { data } = await axios.post(
        `${BIG_PICTURE_URL.replace(':region', 'kr')}/api/v2/request_upload_url`,
        requestBody,
        requestOption,
      );
      if (data) {
        await axios.put(data.data.image.upload_url, fileData, {
          headers: {
            'Content-Type': fileItem.type,
            'X-Amz-Meta-Id': data.data.image.id,
          },
        });
        return data.data.image;
      }
    } catch (_) {
      openToast('파일 전송에 실패했어요');
    } 
  } else {
    const requestBody = {
      user_id: Number(userData.id),
      service: 'rocketchat',
      type: 'chat',
      width: width,
      height: height,
      filepath: `rocketchat/${channelId}/${v4()}.${fileItem.type.replace('image/', '')}`,
      content_type: fileItem.type,
    };
    const requestOption = {
      headers: {
        [AUTH_HEADER_KEY]: qrLoginToken,
        'Content-Type': 'application/json',
      },
    };
    try {
      const { data } = await axios.post(
        `${BIG_PICTURE_URL.replace(':region', 'kr')}/api/v2/request_upload_url`,
        requestBody,
        requestOption,
      );
      if (data) {
        await axios.put(data.data.image.upload_url, fileData, {
          headers: {
            'Content-Type': fileItem.type,
            'X-Amz-Meta-Id': data.data.image.id,
          },
        });
        return data.data.image;
      }
    } catch (_) {
      openToast('파일 전송에 실패했어요');
    }
  }
};

export const AuthAPI = {
  tokenRefresh: async (refToken: string, region: string) => {
    return axios.post(`${KARROTAUTH_HTTP.replace(':region', region)}/v2/auth/refreshtokens`, {
      refresh_token: refToken,
    });
  },
  getUserInfo: async <T extends 'oidc' | 'qr' | undefined>(
    accessToken: string, 
    region: string, 
    tokenType?: T
  ): Promise<UserInfoResponse<T>> => {

    // NOTE(capel): OIDC 토큰 로그인 방식
    if (tokenType === 'oidc') {
      const xUserAgent = generateKarrotUserAgent(
        navigator.userAgent,
        '23.46.00', // APP_VERSION은 고정한다.
        process.env.NODE_ENV !== 'production'
      );
      const decodedToken = jwtDecoder(accessToken);
      const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'X-User-Agent': xUserAgent
      };
      return axios.get(
        `${USER_URL.replace(':region', region)}/user/v3/karrot-users/${decodedToken?.sub}`,
        {
          headers,
          params: {
            'query_option.include_karrot_user_profile': true
          }
        }
      );
    }
    
    // NOTE(capel): 기존 QR 로그인 방식
    const headers = { 
      'x-user-token': accessToken
    };
    return axios.get(`${KARROTAUTH_HTTP.replace(':region', region)}/v2/auth/u`, {
      headers,
    });
  },
};

export const XPropertyAPI = {
  badWords: async () => {
    return axios.get(`${XPROPERTY_URL}/v1/countries/KR/properties/chat_bad_words_regex`);
  },
};

const bizPlatformAuthInstance = axios.create({
  withCredentials: true,
  baseURL: BUSINESS_AUTH_URL,
});
export const businessAuthAPI = {
  getBizToken: () => {
    return bizPlatformAuthInstance.get('/api/v2/auths/tokens');
  },
  getProfiles: () => {
    return bizPlatformAuthInstance.get('/api/v1/profiles');
  },
};

export const getStickers = () => {
  return axios.get('/sticker.json');
};
